// Admin v2 — szöveges oldalsáv (Figma ADM_01/ADM_02 „Sidebar”, 256 px).
// Csoportok: Főoldal · Kezelés · Analitika · Közösség · Haladó; a Hangvezérlés és a Fény & Lézer parkolva.
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Icon } from './icons';
import { Avatar } from './ui';
import logo from './dq-logo.svg';
import { useClub } from './club/clubData';

type Item = { to: string; label: string; icon: string };
type Group = { key: string; label: string; icon: string; items: Item[] };

const MANAGEMENT: Group = {
  key: 'management', label: 'Kezelés', icon: 'SlidersHorizontal', items: [
    { to: '/users', label: 'Felhasználók', icon: 'Users' },
    { to: '/venues', label: 'Helyszínek', icon: 'MapPin' },
    { to: '/clubs', label: 'Klubok', icon: 'Buildings' },
    { to: '/venue-requests', label: 'Üzemeltetők', icon: 'UserCheck' },
    { to: '/admin-chat', label: 'Admin Chat', icon: 'ChatsCircle' },
    { to: '/settings', label: 'Beállítások', icon: 'Gear' },
  ],
};
const ANALYTICS: Group = {
  key: 'analytics', label: 'Analitika', icon: 'TrendUp', items: [
    { to: '/statistics', label: 'Statisztika', icon: 'ChartBar' },
    { to: '/reports', label: 'Bevételi riportok', icon: 'FileText' },
    { to: '/payments', label: 'Fizetések', icon: 'Coins' },
    { to: '/sessions', label: 'Munkamenetek', icon: 'Clock' },
  ],
};
const COMMUNITY: Group = {
  key: 'community', label: 'Közösség', icon: 'UsersThree', items: [
    { to: '/charity', label: 'Jótékonyság', icon: 'HandHeart' },
    { to: '/ideabox', label: 'Ötletláda', icon: 'Lightbulb' },
    { to: '/subscribers', label: 'Feliratkozók', icon: 'Envelope' },
    { to: '/giveaway-draw', label: 'Sorsolás', icon: 'Sparkle' },
    { to: '/community-pages', label: 'Közösségi oldalak', icon: 'UsersThree' },
    { to: '/leaderboard', label: 'Ranglista', icon: 'Trophy' },
    { to: '/tournament', label: 'Versenyek', icon: 'Medal' },
    { to: '/training', label: 'Edzés', icon: 'Target' },
  ],
};
const CLUB_ANALYTICS: Group = {
  key: 'clubAnalytics', label: 'Analitika', icon: 'TrendUp', items: [
    { to: '/club/analytics', label: 'Statisztika', icon: 'ChartBar' },
    { to: '/reports', label: 'Riportok', icon: 'FileText' },
    { to: '/sessions', label: 'Munkamenetek', icon: 'Clock' },
  ],
};
const CLUB_TOP: Item[] = [
  { to: '/club/boards', label: 'Táblák', icon: 'SquaresFour' },
  { to: '/club/operation', label: 'Üzemeltetés', icon: 'Wrench' },
  { to: '/club/customize', label: 'Testreszabás', icon: 'PencilSimpleLine' },
];
const ADVANCED: Group = {
  key: 'advanced', label: 'Haladó', icon: 'Stack', items: [
    { to: '/search', label: 'AI Keresés', icon: 'MagnifyingGlass' },
    { to: '/device-lock', label: 'Eszközzárolás', icon: 'Lock' },
    { to: '/marketplace', label: 'Marketplace', icon: 'Storefront' },
  ],
};

const topCls = (active: boolean) =>
  `flex items-center gap-3 h-[40px] px-[14px] rounded-xl text-[14px] transition border ${active
    ? 'bg-[#00AFF2]/[0.12] border-[#00AFF2]/30 text-white font-semibold'
    : 'border-transparent text-[#B8B8C3] font-medium hover:bg-white/5 hover:text-white'}`;

const subCls = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 h-[34px] pl-[46px] pr-3 rounded-xl text-[13px] transition border ${isActive
    ? 'bg-[#00AFF2]/[0.12] border-[#00AFF2]/30 text-white font-semibold'
    : 'border-transparent text-[#B8B8C3] font-medium hover:bg-white/5 hover:text-white'}`;

interface Props {
  username: string;
  avatarUrl: string | null;
  mobileOpen: boolean;
  onNavigate: () => void;
}

export default function AdminSidebar({ username, avatarUrl, mobileOpen, onNavigate }: Props) {
  const { user, isAdmin, isVenueOperator, isCoach, isClubOwner, signOut } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const { clubs, club, setClubId } = useClub();
  const clubMode = loc.pathname.startsWith('/club') || (isClubOwner && !isAdmin);

  const groups = useMemo(() => {
    const g: Group[] = [];
    if (clubMode) return [CLUB_ANALYTICS, COMMUNITY];
    if (isAdmin) g.push(MANAGEMENT);
    if (isAdmin || isVenueOperator) g.push(ANALYTICS, COMMUNITY);
    if (isAdmin) g.push(ADVANCED);
    return g;
  }, [isAdmin, isVenueOperator, clubMode]);

  const activeGroup = groups.find((g) => g.items.some((i) => loc.pathname.startsWith(i.to)))?.key;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (activeGroup) setOpen((o) => ({ ...o, [activeGroup]: true }));
  }, [activeGroup]);

  const homeActive = clubMode ? loc.pathname === '/club' : loc.pathname === '/dashboard';
  const roleLabel = isAdmin ? 'Admin' : isVenueOperator || isClubOwner ? 'Üzemeltető' : isCoach ? 'Edző' : 'Felhasználó';

  return (
    <aside
      className={`fixed left-0 top-0 z-40 h-screen w-64 flex flex-col bg-[#0b0f14] border-r border-[#31383D] overflow-y-auto transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
    >
      <div className="px-4 pt-7 pb-1 flex flex-col items-center">
        <img src={logo} alt="Dartiqon" className="w-[96px] h-auto" />
        <div className={`mt-2 text-[11px] font-semibold tracking-[0.02em] ${clubMode ? 'text-[#d9a514]' : 'text-[#00AFF2]'}`}>
          {clubMode ? 'KLUB PANEL' : isAdmin ? 'ADMIN PANEL' : 'DARTIQON PANEL'}
        </div>
      </div>

      <nav className="px-4 mt-5 flex flex-col gap-1">
        {clubMode && clubs && clubs.length > 0 && (
          <div className="mb-3">
            <div className="text-[11px] font-medium text-[#8a8f99] mb-1.5 px-1">Aktív helyszín</div>
            <div className="relative">
              <Icon name="MapPin" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B8B8C3] pointer-events-none" />
              <select
                value={club?.id ?? ''}
                onChange={(e) => setClubId(e.target.value)}
                className="appearance-none w-full h-[44px] pl-9 pr-9 rounded-xl bg-[#15191E] border border-[#31383D] text-[13px] font-medium text-white outline-none"
              >
                {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <Icon name="CaretDown" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8B8C3] pointer-events-none" />
            </div>
          </div>
        )}

        <NavLink to={clubMode ? '/club' : '/dashboard'} onClick={onNavigate} className={() => topCls(homeActive)}>
          <Icon name="HouseSimple" size={20} />
          Főoldal
        </NavLink>

        {clubMode && CLUB_TOP.map((i) => (
          <NavLink key={i.to} to={i.to} onClick={onNavigate} className={({ isActive }) => topCls(isActive)}>
            <Icon name={i.icon} size={20} />{i.label}
          </NavLink>
        ))}

        {isAdmin && !clubMode && (
          <NavLink to="/club" onClick={onNavigate} className={({ isActive }) => topCls(isActive)}>
            <Icon name="Storefront" size={20} />Klub panel
          </NavLink>
        )}

        {isCoach && !isAdmin && !clubMode && (
          <NavLink to="/coach-profile" onClick={onNavigate} className={({ isActive }) => topCls(isActive)}>
            <Icon name="GraduationCap" size={20} />Edzői profil
          </NavLink>
        )}

        {groups.map((g) => {
          const isOpen = Boolean(open[g.key]);
          const groupActive = activeGroup === g.key;
          return (
            <div key={g.key}>
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}
                className={`w-full flex items-center gap-3 h-[40px] px-[14px] rounded-xl text-[14px] transition hover:bg-white/5 ${isOpen || groupActive ? 'text-white font-semibold' : 'text-[#B8B8C3] font-medium hover:text-white'}`}
                aria-expanded={isOpen}
              >
                <Icon name={g.icon} size={20} />
                <span className="flex-1 text-left">{g.label}</span>
                <Icon name={isOpen ? 'CaretUp' : 'CaretDown'} size={16} className="text-[#B8B8C3]" />
              </button>
              {isOpen && (
                <div className="flex flex-col gap-1 mt-1 mb-1">
                  {g.items.map((i) => (
                    <NavLink key={i.to} to={i.to} onClick={onNavigate} className={subCls}>
                      <Icon name={i.icon} size={16} />
                      {i.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {!isAdmin && !clubMode && (
          <NavLink to="/analytics" onClick={onNavigate} className={({ isActive }) => topCls(isActive)}>
            <Icon name="TrendUp" size={20} />Analitika
          </NavLink>
        )}
        {isAdmin && clubMode && (
          <NavLink to="/dashboard" onClick={onNavigate} className={() => topCls(false)}>
            <Icon name="ArrowLeft" size={20} />Vissza az admin panelre
          </NavLink>
        )}
      </nav>

      <div className="flex-1 min-h-[24px]" />

      <div className="px-4 pb-5">
        <button
          type="button"
          onClick={() => { onNavigate(); nav('/profile'); }}
          className="w-full flex items-center gap-2.5 p-[13px] rounded-[14px] bg-[#15191E] border border-[#31383D] hover:border-[#00AFF2]/40 transition text-left"
        >
          <Avatar src={avatarUrl} name={username} size={36} />
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-white truncate">{username || user?.email}</span>
            <span className="inline-flex mt-1 h-[23px] items-center px-2.5 rounded-full bg-[#00AFF2]/[0.14] text-[#00AFF2] text-[12px] font-semibold">
              {roleLabel}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => signOut()}
          className="mt-1 w-full flex items-center gap-3 h-[38px] px-[14px] rounded-xl text-[14px] font-medium text-[#B8B8C3] hover:text-white hover:bg-white/5 transition"
        >
          <Icon name="SignOut" size={18} />
          Kijelentkezés
        </button>
      </div>
    </aside>
  );
}
